import React, { useState } from 'react';
import { Collapse, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * ProvenanceTrail Component
 *
 * Displays the complete audit trail for FCA compliance.
 * Maps to the backend ProvenanceTrail structure from provenance_trail.py
 *
 * Expected provenance_trail structure:
 * {
 *   recommendation_id: string,
 *   methodology_source: string,
 *   decision_pathway: string[],
 *   decision_steps: DecisionStep[],
 *   compliance_checks: ComplianceCheckResult[],
 *   compliance_summary: { total_checks, passed, warnings, failed, overall_status },
 *   regulatory_justification: { suitability, assessment, consumer_duty, documentation, communication },
 *   alternatives_considered: { fund_name, rejection_reason, regulatory_basis }[],
 *   audit_metadata: { trail_version, fca_handbook_links, deterministic, methodology_reference }
 * }
 */

const StepTypeIcons = {
  client_input: 'user-check',
  validation: 'clipboard-check',
  risk_tolerance: 'balance-scale',
  risk_capacity: 'chart-line',
  risk_profile: 'shield-alt',
  fund_filtering: 'filter',
  fund_selection: 'check-circle',
  allocation_rule: 'sliders-h',
  compliance_check: 'gavel',
  contribution_calc: 'calculator',
  ai_explanation: 'robot',
  final_recommendation: 'flag-checkered'
};

const StepTypeBadgeColors = {
  client_input: 'info',
  validation: 'secondary',
  risk_tolerance: 'primary',
  risk_capacity: 'primary',
  risk_profile: 'primary',
  fund_filtering: 'warning',
  fund_selection: 'success',
  allocation_rule: 'info',
  compliance_check: 'danger',
  contribution_calc: 'info',
  ai_explanation: 'purple',
  final_recommendation: 'success'
};

const ProvenanceTrail = ({ provenanceTrail }) => {
  const [open, setOpen] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});

  // Handle both old format (steps array) and new format (provenance_trail object)
  if (!provenanceTrail) {
    return null;
  }

  // Extract data from new structure
  const {
    recommendation_id,
    methodology_source,
    decision_steps = [],
    compliance_summary = {},
    regulatory_justification,
    alternatives_considered = [],
    audit_metadata = {}
  } = provenanceTrail;

  const toggleStep = (index) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getComplianceStatusColor = (status) => {
    switch (status) {
      case 'PASS': return 'success';
      case 'WARNING': return 'warning';
      case 'FAIL': return 'danger';
      default: return 'secondary';
    }
  };

  const formatOutputData = (data) => {
    if (!data || typeof data !== 'object') return null;
    return Object.entries(data).map(([key, value]) => (
        <div key={key} className="output-item">
          <span className="output-key">{key.replace(/_/g, ' ')}:</span>
          <span className="output-value">
          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
        </span>
        </div>
    ));
  };

  return (
      <div className="provenance-section mt-4">
        {/* Toggle Header */}
        <div
            className="provenance-toggle d-flex justify-content-between align-items-center p-3 bg-light rounded cursor-pointer"
            onClick={() => setOpen(!open)}
            aria-controls="provenance-content"
            aria-expanded={open}
            style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon="clipboard-check" className="me-3 text-primary" size="lg" />
            <div>
              <div className="fw-semibold">View how the decision is made</div>
              <div className="text-muted small">
                {decision_steps.length} decision steps • {compliance_summary.total_checks || 0} compliance checks
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            {compliance_summary.overall_status && (
                <Badge
                    bg={getComplianceStatusColor(compliance_summary.overall_status)}
                    className="me-3"
                >
                  {compliance_summary.overall_status}
                </Badge>
            )}
            <FontAwesomeIcon
                icon={open ? "chevron-down" : "chevron-right"}
                className="text-muted"
            />
          </div>
        </div>

        <Collapse in={open}>
          <div id="provenance-content" className="mt-3">

            {/* Recommendation ID & Metadata */}
            <div className="mb-4 p-3 bg-light rounded">
              <div className="row">
                <div className="col-md-6">
                  <small className="text-muted">Recommendation ID</small>
                  <div className="fw-mono small">{recommendation_id}</div>
                </div>
                <div className="col-md-6">
                  <small className="text-muted">Methodology Source</small>
                  <div className="small">{methodology_source}</div>
                </div>
              </div>
            </div>

            {/* Decision Steps */}
            <h6 className="mb-3">
              <FontAwesomeIcon icon="route" className="me-2 text-primary" />
              Decision Trail ({decision_steps.length} steps)
            </h6>

            <div className="provenance-steps mb-4">
              {decision_steps.map((step, index) => (
                  <div key={index} className="provenance-step mb-3 border rounded">
                    {/* Step Header */}
                    <div
                        className="step-header d-flex align-items-center p-3 bg-light"
                        onClick={() => toggleStep(index)}
                        style={{ cursor: 'pointer' }}
                    >
                      <div className="step-marker me-3">
                        <Badge bg={StepTypeBadgeColors[step.step_type] || 'secondary'}>
                          {step.step_number}
                        </Badge>
                      </div>
                      <FontAwesomeIcon
                          icon={StepTypeIcons[step.step_type] || 'circle'}
                          className="me-2 text-muted"
                      />
                      <div className="flex-grow-1">
                        <div className="step-title fw-semibold">{step.step_name}</div>
                        <div className="step-description text-muted small">{step.description}</div>
                      </div>
                      {step.regulatory_basis && (
                          <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{step.regulatory_text || step.regulatory_basis}</Tooltip>}
                          >
                            <Badge bg="outline-primary" className="ms-2 border border-primary text-primary">
                              {step.regulatory_basis}
                            </Badge>
                          </OverlayTrigger>
                      )}
                      <FontAwesomeIcon
                          icon={expandedSteps[index] ? "chevron-up" : "chevron-down"}
                          className="ms-3 text-muted"
                      />
                    </div>

                    {/* Step Details (Expandable) */}
                    <Collapse in={expandedSteps[index]}>
                      <div className="step-details p-3 border-top">
                        <div className="row">
                          {/* Input Data */}
                          {step.input_data && Object.keys(step.input_data).length > 0 && (
                              <div className="col-md-6 mb-3">
                                <small className="text-muted fw-semibold">
                                  <FontAwesomeIcon icon="arrow-right" className="me-1" />
                                  Input
                                </small>
                                <div className="mt-1 p-2 bg-light rounded small">
                                  {formatOutputData(step.input_data)}
                                </div>
                              </div>
                          )}

                          {/* Output Data */}
                          {step.output_data && Object.keys(step.output_data).length > 0 && (
                              <div className="col-md-6 mb-3">
                                <small className="text-muted fw-semibold">
                                  <FontAwesomeIcon icon="arrow-left" className="me-1" />
                                  Output
                                </small>
                                <div className="mt-1 p-2 bg-success bg-opacity-10 rounded small">
                                  {formatOutputData(step.output_data)}
                                </div>
                              </div>
                          )}
                        </div>

                        {/* Methodology & Regulatory Info */}
                        <div className="mt-2 pt-2 border-top">
                          <div className="row">
                            <div className="col-md-6">
                              <small className="text-muted">
                                <FontAwesomeIcon icon="file-alt" className="me-1" />
                                Source: {step.methodology_source}
                              </small>
                              {step.methodology_reference && (
                                  <div className="small text-muted">
                                    Reference: {step.methodology_reference}
                                  </div>
                              )}
                            </div>
                            {step.fca_handbook_link && (
                                <div className="col-md-6">
                                  <a
                                      href={step.fca_handbook_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="small"
                                  >
                                    <FontAwesomeIcon icon="external-link-alt" className="me-1" />
                                    FCA Handbook
                                  </a>
                                </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Collapse>
                  </div>
              ))}
            </div>

            {/* Compliance Summary */}
            {compliance_summary && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    <FontAwesomeIcon icon="gavel" className="me-2 text-primary" />
                    Compliance Summary
                  </h6>
                  <div className="row g-3">
                    <div className="col-3">
                      <div className="text-center p-3 bg-light rounded">
                        <div className="fs-4 fw-bold">{compliance_summary.total_checks || 0}</div>
                        <div className="small text-muted">Total Checks</div>
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                        <div className="fs-4 fw-bold text-success">{compliance_summary.passed || 0}</div>
                        <div className="small text-muted">Passed</div>
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                        <div className="fs-4 fw-bold text-warning">{compliance_summary.warnings || 0}</div>
                        <div className="small text-muted">Warnings</div>
                      </div>
                    </div>
                    <div className="col-3">
                      <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
                        <div className="fs-4 fw-bold text-danger">{compliance_summary.failed || 0}</div>
                        <div className="small text-muted">Failed</div>
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {/* Regulatory Justification */}
            {regulatory_justification && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    <FontAwesomeIcon icon="balance-scale" className="me-2 text-primary" />
                    Regulatory Justification
                  </h6>
                  <div className="list-group">
                    {Object.entries(regulatory_justification).map(([key, value]) => (
                        <div key={key} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold text-capitalize">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="small text-muted">{value}</div>
                            </div>
                            <FontAwesomeIcon icon="check" className="text-success" />
                          </div>
                        </div>
                    ))}
                  </div>
                </div>
            )}

            {/* Alternatives Considered */}
            {alternatives_considered && alternatives_considered.length > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    <FontAwesomeIcon icon="times-circle" className="me-2 text-muted" />
                    Alternatives Considered & Rejected ({alternatives_considered.length})
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead className="table-light">
                      <tr>
                        <th>Fund</th>
                        <th>Rejection Reason</th>
                        <th>Regulatory Basis</th>
                      </tr>
                      </thead>
                      <tbody>
                      {alternatives_considered.slice(0, 5).map((alt, index) => (
                          <tr key={index}>
                            <td>{alt.fund_name}</td>
                            <td className="text-muted small">{alt.rejection_reason}</td>
                            <td>
                              <Badge bg="outline-secondary" className="border">
                                <span style={{"color":"black"}}>{alt.regulatory_basis}</span>
                              </Badge>
                            </td>
                          </tr>
                      ))}
                      </tbody>
                    </table>
                    {alternatives_considered.length > 5 && (
                        <div className="text-muted small">
                          ... and {alternatives_considered.length - 5} more
                        </div>
                    )}
                  </div>
                </div>
            )}

            {/* Audit Metadata */}
            {audit_metadata && (
                <div className="p-3 bg-success bg-opacity-10 rounded border border-success border-opacity-25">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="check-circle" className="text-success me-3" size="lg" />
                    <div>
                      <div className="fw-semibold text-success">Fully Auditable Recommendation</div>
                      <div className="small text-muted">
                        Trail version {audit_metadata.trail_version} •
                        {audit_metadata.deterministic ? ' Deterministic' : ''} •
                        Source: {audit_metadata.methodology_reference}
                      </div>
                    </div>
                  </div>
                </div>
            )}

          </div>
        </Collapse>
      </div>
  );
};

export default ProvenanceTrail;